import { ApolloQueryResult } from '@apollo/client';
import { Visibility } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Flex } from 'src/components/Flex';
import {
    CancelSalesInvoice,
    ResendSalesInvoice,
} from 'src/features/finance/salesInvoice/view/components/ViewSalesInvoiceCallToActions/components';
import { GetSalesInvoice } from 'types/graphql';

import { SalesInvoiceAllocatePaymentCallToAction } from './components';

interface CallToActionsProps {
    readonly billingContactEmailAddress: string;
    readonly onView: () => void;
    readonly onAllocatePayment: () => void;
    readonly totalOutstanding: number;
    readonly allocating: boolean;
    readonly setShowPaymentAllocationModel: (value: boolean) => void;
    readonly showPaymentAllocationModal: boolean;
    readonly isSalesInvoiceCreateFailed: boolean;
    readonly isSalesInvoiceProcessing: boolean;
    readonly isSalesInvoiceReleaseFailed: boolean;
    readonly isSalesInvoiceEmailFailed: boolean;
    readonly isAllowedToAllocatePaymentToSalesInvoice: boolean;
    readonly handleReleaseSalesInvoice: () => void;
    readonly handleResubmitToSage: () => void;
    refetchSalesInvoice: (
        variables?: Partial<GraphQLOperationVariables>
    ) => Promise<ApolloQueryResult<GetSalesInvoice>>;
}

export default function ViewSalesInvoiceCallToActions({
    billingContactEmailAddress,
    onView,
    onAllocatePayment,
    totalOutstanding,
    allocating,
    setShowPaymentAllocationModel,
    showPaymentAllocationModal,
    isSalesInvoiceCreateFailed,
    isSalesInvoiceReleaseFailed,
    isSalesInvoiceProcessing,
    isSalesInvoiceEmailFailed,
    isAllowedToAllocatePaymentToSalesInvoice,
    refetchSalesInvoice,
    handleReleaseSalesInvoice,
    handleResubmitToSage,
}: CallToActionsProps) {
    const { palette } = useTheme();

    if (isSalesInvoiceProcessing) return null;

    return (
        <Flex alignItems="center" gap="8px">
            {isSalesInvoiceCreateFailed ? (
                <Button variant="npi-primary" onClick={handleResubmitToSage}>
                    Resubmit to Sage
                </Button>
            ) : isSalesInvoiceReleaseFailed ? (
                <Button variant="npi-primary" onClick={handleReleaseSalesInvoice}>
                    Release Invoice
                </Button>
            ) : (
                <>
                    {isAllowedToAllocatePaymentToSalesInvoice && (
                        <SalesInvoiceAllocatePaymentCallToAction
                            allocating={allocating}
                            onAllocatePayment={onAllocatePayment}
                            setShowPaymentAllocationModel={setShowPaymentAllocationModel}
                            showPaymentAllocationModal={showPaymentAllocationModal}
                            totalOutstanding={totalOutstanding}
                        />
                    )}

                    <CancelSalesInvoice />
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        color="white"
                        bgcolor={palette.orange.main}
                        borderRadius="4px"
                        padding="8px"
                        sx={{ cursor: 'pointer' }}
                        onClick={onView}
                        data-testid="view-sales-invoice-button"
                    >
                        <Visibility height="24px" width="24px" />
                    </Flex>
                    <ResendSalesInvoice
                        refetchSalesInvoice={refetchSalesInvoice}
                        billingContactEmailAddress={billingContactEmailAddress}
                        isSalesInvoiceEmailFailed={isSalesInvoiceEmailFailed}
                    />
                </>
            )}
        </Flex>
    );
}
